import { useEffect, useState } from 'react';
import { GraduationCap, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';

type Section = { id: string; key: string; nameEn: string; nameEs: string; kind: string };

/**
 * "Send to Training" (mig 0472, pedido de Manuel 2026-10-02): la grabación cae al INBOX de
 * Training con el departamento SUGERIDO y una nota; los super-managers la mueven a su sección y le
 * escriben la explicación. Lo usan la vista previa tras grabar y la tarjeta de "Mis grabaciones".
 */
export function SendToTrainingPanel({ recordingId, onDone, onSent }: { recordingId: number; onDone: () => void; onSent?: () => void }) {
  const { t, lang } = useI18n();
  const [sections, setSections] = useState<Section[]>([]);
  const [sec, setSec] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    api.get<{ sections: Section[] }>('/api/training')
      .then((r) => { if (alive) setSections(r.sections.filter((s) => s.kind !== 'inbox')); })
      .catch(() => { if (alive) setSections([]); });
    return () => { alive = false; };
  }, []);

  const send = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.post('/api/training/from-recording', { recordingId, sectionId: sec || null, note: note.trim() || null });
      setMsg({ ok: true, text: t('trn.sent') });
      onSent?.();
      window.setTimeout(onDone, 1200);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError && e.code === 'already_in_training' ? t('trn.alreadySent') : t('trn.saveFailed') });
    } finally { setBusy(false); }
  };

  return (
    <div className="rec-panel">
      <div className="rec-panel-head">
        <b style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><GraduationCap size={15} strokeWidth={1.9} /> {t('trn.sendTitle')}</b>
        <button type="button" className="btn ghost sm ash-open" onClick={onDone} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button>
      </div>
      <div className="rec-attach-form">
        <p className="ss-hint" style={{ margin: 0 }}>{t('trn.sendHint')}</p>
        <label className="rec-attach-row">
          <span>{t('trn.sendDept')}</span>
          <select className="input sm" value={sec} onChange={(e) => setSec(e.target.value)}>
            <option value="">{t('trn.sendDeptAny')}</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{lang === 'es' ? s.nameEs : s.nameEn}</option>)}
          </select>
        </label>
        <textarea className="input sm" rows={2} maxLength={2000} placeholder={t('trn.sendNotePh')} value={note} onChange={(e) => setNote(e.target.value)} />
        {msg && <div className={msg.ok ? 'trn-ok' : 'trn-err'} role="status">{msg.text}</div>}
        <div className="rec-panel-actions">
          <button type="button" className="btn sm" disabled={busy || msg?.ok} onClick={() => { void send(); }}><GraduationCap size={14} strokeWidth={1.9} /> {t('trn.sendBtn')}</button>
        </div>
      </div>
    </div>
  );
}
